import { sortBy, truncate, uniqBy } from "lodash";
import moment from "moment";

import { TopicflowIconType } from "@components/topicflow-icon/topicflow-icon-helpers";

export type PromptSuggestion = {
  key: string;
  label: string;
  prompt: string;
  icon: TopicflowIconType;
};

export type SuggestionPerson = {
  id: number;
  name: string;
  firstName?: string | null;
};

export type SuggestionMeeting = {
  id: number;
  title?: string | null;
  startDatetime?: string | null;
  endDatetime?: string | null;
  isFormalOneonone: boolean;
  /** Participants with a Topicflow user, minus the current user. */
  otherParticipants: SuggestionPerson[];
};

// The homepage shows at most 3 starter prompts. Meeting prep comes first, then
// the skill prompts fill whatever is left.
export const maxPromptSuggestions = 3;
const maxMeetingTitleLength = 34;

const firstNameOf = (person: SuggestionPerson) =>
  person.firstName?.trim() || person.name.split(" ")[0] || person.name;

// "2:00pm" reads better as "2pm"; keep the minutes when they matter.
const formatTime = (start: moment.Moment) =>
  start.format("h:mma").replace(":00", "");

/** Human phrase for when a meeting happens: "today at 2pm", "tomorrow at
 * 9:30am", or "on Thu, Aug 14 at 10am". Used inside the prompt so Topicflow AI
 * can pick the exact meeting. */
export const formatMeetingWhen = (
  startDatetime: string,
  now: moment.Moment,
): string => {
  const start = moment(startDatetime);
  const time = formatTime(start);
  if (start.isSame(now, "day")) return `today at ${time}`;
  if (start.isSame(moment(now).add(1, "day"), "day")) {
    return `tomorrow at ${time}`;
  }
  return `on ${start.format("ddd, MMM D")} at ${time}`;
};

/** Prompts for the routine-style skills we offer when there is no meeting to
 * prep. These send the `/command` form on purpose: unlike prep or overview,
 * these skills are not in the model's `load_skill` list, so the slash command is
 * the only reliable way to run them. The caller shuffles the order. */
export const buildSkillSuggestions = ({
  recognitionLabel,
}: {
  recognitionLabel: string;
}): PromptSuggestion[] => [
  {
    key: "skill-daily-brief",
    icon: "ai",
    label: "Give me my daily brief",
    prompt: "/daily-brief",
  },
  {
    // eslint-disable-next-line local-rules/use-label -- skill slug, not copy; the label uses recognitionLabel
    key: "skill-recognition-opportunities",
    icon: "ai",
    label: `Find ${recognitionLabel} opportunities`,
    prompt: "/recognition-opportunities",
  },
  {
    key: "skill-feedback-opportunities",
    icon: "ai",
    label: "Find feedback opportunities",
    prompt: "/feedback-opportunities",
  },
];

/** Build the starter prompts shown under the homepage Ask AI input (TF-1590).
 *
 * Meeting prep is the most useful thing to offer, so the next 1-on-1 and the
 * next group meeting come first (in the order they happen), then any further
 * upcoming meetings. The prompts are plain English and name the person/title and
 * the time, so the model resolves the right meeting. When there is no meeting to
 * prep, `skillSuggestions` (shuffled by the caller) fills the row instead.
 */
export const buildPromptSuggestions = ({
  meetings,
  skillSuggestions,
  oneononeLabel,
  now,
}: {
  meetings: SuggestionMeeting[];
  skillSuggestions: PromptSuggestion[];
  oneononeLabel: string;
  now: moment.Moment;
}): PromptSuggestion[] => {
  // Keep meetings that are upcoming or still in progress; drop finished ones.
  const relevantMeetings = sortBy(
    meetings.filter(
      (meeting) =>
        !!meeting.startDatetime &&
        !(meeting.endDatetime && moment(meeting.endDatetime).isBefore(now)),
    ),
    "startDatetime",
  );

  const canPrepOneonone = (meeting: SuggestionMeeting) =>
    meeting.isFormalOneonone && meeting.otherParticipants.length > 0;
  const canPrepGroupMeeting = (meeting: SuggestionMeeting) =>
    !meeting.isFormalOneonone && !!meeting.title?.trim();

  const nextOneonone = relevantMeetings.find(canPrepOneonone);
  const nextGroupMeeting = relevantMeetings.find(canPrepGroupMeeting);

  // Lead with one of each kind so a manager sees both, then top up with the
  // meetings that come soonest. Everything stays in start-time order.
  const meetingsToPrep = uniqBy(
    [
      ...sortBy(
        [nextOneonone, nextGroupMeeting].filter(
          (meeting): meeting is SuggestionMeeting => !!meeting,
        ),
        "startDatetime",
      ),
      ...relevantMeetings.filter(
        (meeting) => canPrepOneonone(meeting) || canPrepGroupMeeting(meeting),
      ),
    ],
    "id",
  ).slice(0, maxPromptSuggestions);

  const suggestions = meetingsToPrep.map((meeting) => {
    const when = formatMeetingWhen(meeting.startDatetime as string, now);
    if (canPrepOneonone(meeting)) {
      const person = meeting.otherParticipants[0];
      return {
        key: `oneonone-prep-${meeting.id}`,
        icon: "calendar" as TopicflowIconType,
        label: `Prep my ${oneononeLabel} with ${firstNameOf(person)}`,
        prompt: `Help me prep for my ${oneononeLabel} with ${person.name} ${when}.`,
      };
    }
    const title = (meeting.title || "").trim();
    return {
      key: `meeting-prep-${meeting.id}`,
      icon: "calendar" as TopicflowIconType,
      label: `Prep for ${truncate(title, { length: maxMeetingTitleLength })}`,
      prompt: `Help me prep for my meeting "${title}" ${when}.`,
    };
  });

  skillSuggestions.forEach((suggestion) => {
    if (suggestions.length < maxPromptSuggestions) suggestions.push(suggestion);
  });

  return suggestions.slice(0, maxPromptSuggestions);
};
